<template>
  <div class="doc-tree-node">
    <div
      v-if="isDir"
      class="doc-tree-row doc-tree-row-dir"
      :style="rowStyle"
      v-draggable="dirDragId"
      v-droppable="editorMode ? handleDropIn : null"
      :data-dir-id="node.id"
      @click.stop="onDirRowClick"
      @contextmenu.prevent="onDirContextMenu($event)"
    >
      <Icon
        :type="isExpanded ? 'ios-arrow-down' : 'ios-arrow-forward'"
        class="doc-tree-expand"
        @click.stop="toggleExpanded"
      />
      <BaseIcon icon="md-folder" :size="15" class="doc-tree-icon" />
      <span class="doc-tree-label">{{ node.name }}</span>
    </div>

    <div
      v-else
      class="doc-tree-row doc-tree-row-doc"
      :class="{ 'is-selected': isSelected }"
      :style="rowStyle"
      v-draggable="fileDragId"
      @click.stop="handleSelect"
      @contextmenu.prevent="editorMode && showFileContextMenu($event)"
    >
      <span class="doc-tree-expand doc-tree-expand-placeholder"></span>
      <BaseIcon :icon="icon" :size="15" class="doc-tree-icon" />
      <span class="doc-tree-label">{{ node.name || '未命名文档' }}</span>
      <span v-if="editorMode && !isEditable" class="doc-tree-tag">附件</span>
    </div>

    <div v-if="isDir && isExpanded && hasChildren" class="doc-tree-children">
      <document-tree-node
        v-for="child in children"
        :key="childKey(child)"
        :node="child"
        :level="level + 1"
        :selected-id="selectedId"
        :root-id="rootId"
        :expand-all="expandAll"
        :editor-mode="editorMode"
        :preview-mode="previewMode"
        @select="$emit('select', $event)"
        @dir-action="$emit('dir-action', $event)"
        @file-action="$emit('file-action', $event)"
        @drop-in="$emit('drop-in', $event)"
      />
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/Icon';
import {
  getDocumentIcon,
  getTreeDisplayChildren,
  isDirNode,
  isSelectableFileNode,
  isEditableDocument,
} from './document-helpers';

export default {
  name: 'DocumentTreeNode',
  components: {BaseIcon},
  props: {
    node: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      default: 0,
    },
    selectedId: {
      type: [String, Number],
      default: '',
    },
    rootId: {
      type: [String, Number],
      default: '',
    },
    editorMode: {
      type: Boolean,
      default: false,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    expandAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: true,
    };
  },
  computed: {
    isDir() {
      return isDirNode(this.node);
    },
    children() {
      return getTreeDisplayChildren(this.node);
    },
    hasChildren() {
      return this.children.length > 0;
    },
    rowStyle() {
      return {paddingLeft: `${this.level * 14 + 8}px`};
    },
    icon() {
      return getDocumentIcon(this.node);
    },
    isSelected() {
      return this.selectedId != null
        && this.selectedId !== ''
        && String(this.selectedId) === String(this.node.id);
    },
    isRootDir() {
      if (!this.isDir) return false;
      if (this.rootId != null && this.rootId !== '') {
        return String(this.node.id) === String(this.rootId);
      }
      return this.level === 0;
    },
    isEditable() {
      return isEditableDocument(this.node);
    },
    isExpanded() {
      return this.expandAll || this.expanded;
    },
    dirDragId() {
      if (!this.editorMode || !this.isDir || this.isRootDir) return null;
      return this.node.id;
    },
    fileDragId() {
      if (!this.editorMode || this.isDir) return null;
      if (!isSelectableFileNode(this.node)) return null;
      return this.node.id;
    },
  },
  methods: {
    childKey(child) {
      return child.id != null ? child.id : `${child.name}-${this.level}`;
    },
    toggleExpanded() {
      if (this.expandAll) return;
      this.expanded = !this.expanded;
    },
    onDirRowClick() {
      this.toggleExpanded();
    },
    handleSelect() {
      if (!isSelectableFileNode(this.node)) return;
      this.$emit('select', this.node);
    },
    onDirContextMenu(e) {
      if (this.editorMode) {
        this.showDirContextMenu(e);
      } else if (this.previewMode) {
        this.showPreviewDirContextMenu(e);
      }
    },
    showPreviewDirContextMenu(e) {
      const dir = this.node;
      this.$contextmenu({
        event: e,
        items: [
          {
            label: '单独打开',
            onClick: () => {
              this.$emit('dir-action', {action: 'open-preview', dir});
            },
          },
        ],
        zIndex: 9999,
        minWidth: 120,
      });
    },
    showDirContextMenu(e) {
      const dir = this.node;
      const isRoot = this.isRootDir;
      this.$contextmenu({
        event: e,
        items: [
          {
            label: '新增子目录',
            disabled: isRoot && dir.enable_create_sub === false,
            onClick: () => {
              this.$emit('dir-action', {action: 'add', dir});
            },
          },
          {
            label: '新建 Markdown',
            onClick: () => {
              this.$emit('dir-action', {action: 'add-doc', dir, docType: 'md'});
            },
          },
          {
            label: '新建文本',
            onClick: () => {
              this.$emit('dir-action', {action: 'add-doc', dir, docType: 'text'});
            },
          },
          {
            label: '上传文件',
            onClick: () => {
              this.$emit('dir-action', {action: 'upload-files', dir});
            },
          },
          {
            label: '编辑目录',
            disabled: isRoot,
            onClick: () => {
              this.$emit('dir-action', {action: 'edit', dir});
            },
          },
          {
            label: '删除目录',
            disabled: isRoot,
            onClick: () => {
              this.$emit('dir-action', {action: 'remove', dir});
            },
          },
        ],
        zIndex: 9999,
        minWidth: 130,
      });
    },
    showFileContextMenu(e) {
      const file = this.node;
      const items = [
        {
          label: '重命名',
          onClick: () => {
            this.$emit('file-action', {action: 'rename', file});
          },
        },
        {
          label: '删除',
          onClick: () => {
            this.$emit('file-action', {action: 'remove', file});
          },
        },
      ];
      this.$contextmenu({
        event: e,
        items,
        zIndex: 9999,
        minWidth: 100,
      });
    },
    handleDropIn(draggedId) {
      if (!draggedId || !this.node || !this.node.id) return;
      if (String(draggedId) === String(this.node.id)) return;
      this.$emit('drop-in', {
        draggedId,
        targetDirId: this.node.id,
      });
    },
  },
};
</script>

<style lang="less" scoped>
@doc-tree-text: #303133;
@doc-tree-text-secondary: #606266;
@doc-tree-text-muted: #909399;
@doc-tree-primary: #2d8cf0;

.doc-tree-row {
  display: flex;
  align-items: center;
  min-height: 32px;
  padding-right: 8px;
  user-select: none;
  transition: background 0.15s ease;
  color: @doc-tree-text;
}

.doc-tree-row-dir {
  cursor: pointer;
  font-weight: 500;
  border-radius: 0;

  .doc-tree-icon {
    color: #e6a23c;
  }

  .doc-tree-expand {
    color: @doc-tree-text-secondary;
    cursor: pointer;
  }

  .doc-tree-label {
    color: @doc-tree-text;
  }

  &:hover {
    background: rgba(45, 140, 240, 0.08);

    .doc-tree-label {
      color: @doc-tree-text;
    }
  }
}

.doc-tree-row-doc {
  cursor: pointer;
  border-radius: 6px;

  .doc-tree-icon {
    color: @doc-tree-text-secondary;
  }

  .doc-tree-label {
    color: @doc-tree-text;
  }

  &:hover {
    background: rgba(45, 140, 240, 0.08);

    .doc-tree-label,
    .doc-tree-icon {
      color: @doc-tree-primary;
    }
  }

  &.is-selected {
    background: rgba(45, 140, 240, 0.12);
    font-weight: 500;

    .doc-tree-label,
    .doc-tree-icon {
      color: @doc-tree-primary;
    }
  }
}

.doc-tree-expand {
  width: 16px;
  flex-shrink: 0;
  font-size: 12px;
  color: @doc-tree-text-secondary;
}

.doc-tree-expand-placeholder {
  display: inline-block;
}

.doc-tree-icon {
  margin-right: 6px;
  flex-shrink: 0;
}

.doc-tree-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: @doc-tree-text;
}

.doc-tree-tag {
  font-size: 11px;
  color: @doc-tree-text-muted;
  margin-left: 6px;
  flex-shrink: 0;
}

.doc-tree-row.ugo-draggable {
  opacity: 0.85;
}

.doc-tree-row.ugo-droppable {
  background: rgba(45, 140, 240, 0.12);
  outline: 1px dashed @doc-tree-primary;
}
</style>
